import React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import ChartTooltip from "./ChartTooltip";
import ChartLegend from "./ChartLegend";
import { series, chrome } from "./chartTheme";

/**
 * Donut with a hero figure in the hole and a legend carrying the counts.
 *
 * Segments are separated by a 2px ring in the surface colour, not by an outline
 * - the gap is what does the separating. Identity comes from the legend, never
 * from colour alone.
 *
 * @param {{name: string, value: number}[]} data
 * @param {string[]} colors one colour per slice, in fixed slot order
 */
export default function DonutChart({
  data = [],
  colors,
  height = 240,
  centerValue,
  centerLabel,
  total,
}) {
  const theme = chrome();
  const sum = total ?? data.reduce((acc, d) => acc + (d.value || 0), 0);
  const palette = colors || series(data.length);

  const colorAt = (i) => palette[i] || theme.muted;

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="88%"
            stroke={theme.surface}
            strokeWidth={2}
            isAnimationActive={false}
          >
            {data.map((entry, i) => (
              <Cell key={entry.name} fill={colorAt(i)} />
            ))}
          </Pie>

          {centerValue !== undefined && (
            <text
              x="50%"
              y="50%"
              textAnchor="middle"
              dominantBaseline="middle"
              className="viz-donut-center-value"
            >
              {centerValue}
            </text>
          )}
          {centerLabel && (
            <text
              x="50%"
              y="50%"
              dy="22"
              textAnchor="middle"
              dominantBaseline="middle"
              className="viz-donut-center-label"
            >
              {centerLabel}
            </text>
          )}

          <Tooltip content={<ChartTooltip total={sum} />} />
        </PieChart>
      </ResponsiveContainer>

      <ChartLegend
        items={data.map((d, i) => ({
          name: d.name,
          value: d.value,
          color: colorAt(i),
        }))}
      />
    </div>
  );
}
